import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useElementWidth } from '../hooks/useElementWidth';
import { prefersReducedMotion } from '../utils/motion';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader2, BarChart3 } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const { login, loginWithGoogle, error, isLoading } = useAuth();
  useDocumentTitle('Sign in');
  const googleEnabled = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const cardRef = useRef<HTMLDivElement>(null);
  // Google draws its button at a fixed pixel width (200 to 400): fit it to the card instead of a number that overflows a phone.
  const [googleSlot, slotWidth] = useElementWidth<HTMLDivElement>();
  const googleWidth = Math.max(200, Math.min(336, slotWidth ?? 336));
  const formRef = useRef<HTMLFormElement>(null);

  // Subtle entrance (not for someone who has asked for less motion: the card is simply there)
  useEffect(() => {
    if (prefersReducedMotion()) return;
    gsap.fromTo(
      cardRef.current,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out' }
    );
  }, []);

  // The error line says what went wrong; the shake only underlines it, so it is left out when motion is reduced.
  const shake = () => {
    if (prefersReducedMotion()) return;
    gsap.fromTo(formRef.current, { x: -8 }, { x: 0, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
  };

  const busy = isSubmitting || isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!username.trim() || !password.trim()) {
      setLocalError('Please fill in all fields');
      shake();
      return;
    }

    try {
      setIsSubmitting(true);
      await login(username.trim(), password);
      // Nothing to do on success: the /login route sees the signed-in person and sends them on (LoginRoute).
    } catch (err: any) {
      setLocalError(err.message);
      shake();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credential?: string) => {
    setLocalError(null);
    if (!credential) {
      setLocalError('Google sign-in failed. Please try again.');
      return;
    }
    try {
      setIsSubmitting(true);
      await loginWithGoogle(credential);
      // Nothing to do on success: the /login route sees the signed-in person and sends them on (LoginRoute).
    } catch (err: any) {
      setLocalError(err.message);
      shake();
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = localError || error;

  const inputBase =
    'w-full rounded-xl border border-secondary-200 bg-white py-3 pl-11 text-sm text-secondary-900 ' +
    'placeholder-secondary-500 shadow-sm transition-all focus:border-primary-500 focus:outline-none ' +
    'focus:ring-4 focus:ring-primary-100 disabled:opacity-60';

  return (
    <main className="relative min-h-screen overflow-hidden bg-secondary-50">
      {/* soft ambient glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary-200/40 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 translate-x-1/3 translate-y-1/3 rounded-full bg-primary-100/50 blur-3xl" />

      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div ref={cardRef} className="w-full max-w-[400px]">
          {/* Brand */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 shadow-lg shadow-primary-600/25">
              <BarChart3 className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-secondary-900">Welcome back</h1>
            <p className="mt-1.5 text-sm text-secondary-500">Sign in to your worklog dashboard</p>
          </div>

          {/* Card */}
          <div className="rounded-2xl border border-secondary-100 bg-white/80 p-6 shadow-xl shadow-secondary-900/5 backdrop-blur-sm sm:p-8">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              {googleEnabled && (
                <>
                  {/* Google */}
                  <div ref={googleSlot} className="flex justify-center">
                    <div className="max-w-full overflow-hidden rounded-full shadow-sm ring-1 ring-secondary-200 transition-shadow hover:shadow-md">
                      <GoogleLogin
                        onSuccess={(cred) => handleGoogleSuccess(cred.credential)}
                        onError={() => setLocalError('Google sign-in failed. Please try again.')}
                        theme="outline"
                        size="large"
                        shape="pill"
                        text="continue_with"
                        logo_alignment="center"
                        width={String(googleWidth)}
                      />
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-secondary-200" />
                    <span className="text-xs font-medium text-secondary-500">Or sign in with email</span>
                    <div className="h-px flex-1 bg-secondary-200" />
                  </div>
                </>
              )}

              {/* Email / username */}
              <div>
                <label htmlFor="username" className="mb-1.5 block text-xs font-medium text-secondary-600">
                  Email or username
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={`${inputBase} pr-4`}
                    placeholder="you@jarvis.consulting"
                    autoComplete="username"
                    disabled={busy}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-secondary-600">
                  Password
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputBase} pr-11`}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={busy}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-secondary-500 transition-colors hover:bg-secondary-50 hover:text-secondary-600"
                    tabIndex={-1}
                    disabled={busy}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {displayError && (
                <div className="flex items-start gap-2 rounded-xl border border-error-200 bg-error-50 px-3.5 py-2.5 text-error-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{displayError}</span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={busy}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 py-3 text-sm font-semibold text-white shadow-sm shadow-primary-600/25 transition-all hover:bg-primary-700 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Signing in…</span>
                  </>
                ) : (
                  <span>Sign in</span>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-xs text-secondary-500">
            Secure authentication · Jarvis Jira
          </p>
        </div>
      </div>
    </main>
  );
};

export default LoginPage;
